/** The catalogue as the pages and the menu read it: top categories, in Tebex's order. */

import { useMemo } from 'react'

import type { Category } from './api'
import { useStore } from './state'

export const byOrder = <T extends { order?: number; id: number }>(a: T, b: T) =>
  (a.order ?? 0) - (b.order ?? 0) || a.id - b.id

export interface Shelf {
  category: Category
  children: Category[]
}

export const packagesOf = (category: Category) => [...(category.packages ?? [])].sort(byOrder)

/** A shelf with something on it, in the category or under it. */
export const filled = ({ category, children }: Shelf) =>
  packagesOf(category).length > 0 || children.some((child) => packagesOf(child).length > 0)

/** Top categories in their order, each with the subcategories under it. */
export function useShelves(): Shelf[] | undefined {
  const categories = useStore((state) => state.categories)
  return useMemo(() => {
    if (!categories) return undefined
    const sorted = [...categories].sort(byOrder)
    return sorted
      .filter((category) => !category.parent)
      .map((category) => ({
        category,
        children: sorted.filter((child) => child.parent?.id === category.id),
      }))
  }, [categories])
}

/**
 * What the home page and the menu show, one section each: a top category with packages
 * of its own, with its subcategories under it; a top category that only groups others
 * ("Server" holding "Veicoli") gives way to them.
 */
export function useSections(): Shelf[] | undefined {
  const shelves = useShelves()
  return useMemo(() => {
    if (!shelves) return undefined
    return shelves
      .filter(filled)
      .flatMap((shelf) =>
        packagesOf(shelf.category).length > 0 || shelf.children.length === 0
          ? [shelf]
          : shelf.children
              .filter((child) => packagesOf(child).length > 0)
              .map((child) => ({ category: child, children: [] })),
      )
  }, [shelves])
}

/** The section a category belongs to: itself, or the section above it. */
export function sectionOf(sections: Shelf[], categories: Category[], id: number): number | null {
  if (sections.some((section) => section.category.id === id)) return id
  const parent = categories.find((each) => each.id === id)?.parent
  return parent ? sectionOf(sections, categories, parent.id) : null
}
